"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router";
import { MessageCircle } from "lucide-react";
import { FlipWords } from "@/components/ui/flip-words";
import { WorkerResultsPanel } from "./worker-results-panel";
import { workers, teams, categories } from "@/constants/workers";
import type { PublicWorker, PublicTeam } from "@/constants/workers";

// FlipWords — SEO-driven roles that map to real worker profile pages
const heroFlipWords = ["Workforce", "Team", "Department", "Content Writer", "Cold Email Setter", "DM Receptionist", "Lead Gen Researcher", "Calendar Manager", "SEO Analyst"];

// Workers sourced from real DB export — no hardcoded catalog
const ALL_WORKERS = workers;

// Popular suggestions: top 4 by executions
const POPULAR_WORKERS = workers
    .filter((w) => w.stats && w.stats.totalExecutions > 0)
    .slice(0, 4);

// Popular teams: top 3 by executions
const POPULAR_TEAMS = teams
    .filter((t) => t.stats.totalExecutions > 0)
    .slice(0, 3);

const PLATFORM_URL = "https://poly.agencyofpoly.com";

export function WorkerSearchHero() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [searchedQuery, setSearchedQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLElement>(null);

    // Scroll-driven fade: section fades out as user scrolls past it
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const sectionOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 1, 0]);
    const sectionY = useTransform(scrollYProgress, [0, 0.7, 1], [0, 0, -60]);

    // Filter workers based on query (limit to top 12 for dropdown)
    const filteredWorkers = query.trim().length > 0
        ? ALL_WORKERS.filter(
            (w) =>
                w.name.toLowerCase().includes(query.toLowerCase()) ||
                w.role.toLowerCase().includes(query.toLowerCase()) ||
                w.department.toLowerCase().includes(query.toLowerCase()) ||
                w.category.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 12)
        : [];

    // Filter teams based on query
    const filteredTeams = query.trim().length > 0
        ? teams.filter(
            (t) =>
                t.name.toLowerCase().includes(query.toLowerCase()) ||
                t.description.toLowerCase().includes(query.toLowerCase()) ||
                t.specializations.some((s) => s.toLowerCase().includes(query.toLowerCase()))
        ).slice(0, 4)
        : [];

    // Group filtered workers by category
    const groupedResults = filteredWorkers.reduce<Record<string, typeof filteredWorkers>>((acc, w) => {
        if (!acc[w.category]) acc[w.category] = [];
        acc[w.category].push(w);
        return acc;
    }, {});

    const hasQuery = query.trim().length > 0;
    const showDropdown = isFocused;

    const handleSearch = useCallback(() => {
        if (query.trim().length === 0) return;
        setSearchedQuery(query.trim());
        setShowResults(true);
        setIsFocused(false);
        inputRef.current?.blur();
    }, [query]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                handleSearch();
            }
            if (e.key === "Escape") {
                setIsFocused(false);
                inputRef.current?.blur();
            }
        },
        [handleSearch]
    );

    // Select a suggestion — navigate to the worker profile page
    const handleSelectWorker = useCallback((worker: PublicWorker) => {
        setIsFocused(false);
        inputRef.current?.blur();
        navigate(`/workers/${worker.slug}`);
    }, [navigate]);

    // Select a team — navigate to the team profile page
    const handleSelectTeam = useCallback((team: PublicTeam) => {
        setIsFocused(false);
        inputRef.current?.blur();
        navigate(`/teams/${team.slug}`);
    }, [navigate]);

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(e.target as Node)
            ) {
                setIsFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const TOTAL_WORKER_COUNT = ALL_WORKERS.length;

    // Category quick-filters derived from real data
    const CATEGORIES = categories.slice(0, 6).map((cat) => {
        const icons: Record<string, string> = {
            "Sales & Outreach": "📈",
            "Content & Creative": "✍️",
            "Operations & Admin": "⚙️",
            "Research & Data": "🔍",
            "Customer & Support": "💬",
            "Engineering & DevOps": "🛠️",
            "Strategy & Planning": "🎯",
            "Research & Automation": "🤖",
        };
        return { label: cat, icon: icons[cat] || "⚡" };
    });

    const handleCategoryClick = useCallback((category: string) => {
        navigate(`/workers?category=${encodeURIComponent(category)}`);
    }, [navigate]);

    return (
        <div>
            <section id="home" ref={sectionRef} className="relative min-h-screen flex flex-col items-center justify-center bg-background px-4 pt-24 pb-12">
                <motion.div
                    className="flex flex-col items-center justify-center w-full"
                    style={{ opacity: sectionOpacity, y: sectionY }}
                >
                    {/* Radial glow behind headline */}
                    <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />

                    {/* Headline with FlipWords */}
                    <motion.div
                        className="relative z-10 text-center mb-4"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extralight tracking-tight text-foreground leading-tight">
                            Hire Your Digital
                            <br />
                            <FlipWords words={heroFlipWords} duration={2500} className="font-light" />
                        </h1>
                    </motion.div>

                    {/* Subheadline */}
                    <motion.p
                        className="relative z-10 text-base sm:text-lg text-muted-foreground font-light text-center max-w-lg mb-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                    >
                        Hire AI workers that handle your sales, content, ops &amp; support — 24/7.
                    </motion.p>

                    {/* Compact social proof line: count + MS badge + status */}
                    <motion.div
                        className="relative z-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-6"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.25 }}
                    >
                        <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground font-light">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-card border border-border text-xs font-semibold text-foreground">
                                {TOTAL_WORKER_COUNT}+
                            </span>
                            digital workers
                        </span>
                        <span className="text-foreground/20">·</span>
                        <span className="text-xs sm:text-sm text-muted-foreground font-light">Part of Microsoft for Startups</span>
                        <span className="text-foreground/20">·</span>
                        <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground font-light">
                            Available Now
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                            </span>
                        </span>
                    </motion.div>

                    {/* Search bar */}
                    <motion.div
                        className="relative z-20 w-full max-w-2xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.35 }}
                    >
                        <div
                            className={`relative flex items-center rounded-2xl border bg-card transition-all duration-200 ${isFocused
                                ? "border-primary shadow-[0_0_0_2px_rgba(99,102,241,0.15),0_8px_40px_rgba(0,0,0,0.3)]"
                                : "border-border shadow-[0_0_24px_rgba(34,42,53,0.06),0_1px_1px_rgba(0,0,0,0.05)]"
                                }`}
                        >
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onKeyDown={handleKeyDown}
                                placeholder="Hire digital workers..."
                                className="flex-1 bg-transparent px-5 py-4 text-sm md:text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none font-sans"
                                autoComplete="off"
                            />
                            <button
                                onClick={handleSearch}
                                className="mr-2 flex items-center gap-2 rounded-xl bg-foreground text-background px-3 sm:px-5 py-2.5 text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.35-4.35" />
                                </svg>
                                <span className="hidden sm:inline">Hire</span>
                            </button>
                        </div>

                        {/* Suggestions dropdown */}
                        <AnimatePresence>
                            {showDropdown && isFocused && (
                                <motion.div
                                    ref={dropdownRef}
                                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                                    transition={{ duration: 0.15, ease: "easeOut" }}
                                    className="absolute left-0 right-0 top-full mt-2 rounded-2xl border border-border bg-card shadow-[0_16px_68px_rgba(0,0,0,0.25)] overflow-hidden z-50"
                                >
                                    {hasQuery && (Object.keys(groupedResults).length > 0 || filteredTeams.length > 0) ? (
                                        // Search results grouped by category + teams
                                        <div className="py-2 max-h-80 overflow-y-auto">
                                            {/* Team results */}
                                            {filteredTeams.length > 0 && (
                                                <div>
                                                    <div className="px-5 py-2 flex items-center gap-3">
                                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                            Digital Teams
                                                        </span>
                                                        <span className="text-xs text-muted-foreground/60">
                                                            {filteredTeams.length} {filteredTeams.length === 1 ? "team" : "teams"}
                                                        </span>
                                                    </div>
                                                    {filteredTeams.map((team) => (
                                                        <button
                                                            key={team.slug}
                                                            onClick={() => handleSelectTeam(team)}
                                                            className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-2.5 hover:bg-accent transition-colors cursor-pointer text-left"
                                                        >
                                                            <div className="flex h-9 w-9 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20 shrink-0">
                                                                <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                                    <circle cx="9" cy="7" r="4" />
                                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-foreground">{team.name}</div>
                                                                <div className="text-xs text-muted-foreground truncate">
                                                                    {team.memberCount} workers · {team.specializations.slice(0, 2).join(", ")}
                                                                </div>
                                                            </div>
                                                            <span className="text-xs text-muted-foreground/60 hidden sm:block">
                                                                {team.stats.totalExecutions.toLocaleString()} exec
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Worker results by category */}
                                            {Object.entries(groupedResults).map(([category, catWorkers]) => (
                                                <div key={category}>
                                                    <div className="px-5 py-2 flex items-center gap-3">
                                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                            {category}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground/60">
                                                            {catWorkers.length} {catWorkers.length === 1 ? "worker" : "workers"}
                                                        </span>
                                                    </div>
                                                    {catWorkers.map((worker) => (
                                                        <button
                                                            key={worker.name}
                                                            onClick={() => handleSelectWorker(worker)}
                                                            className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-2.5 hover:bg-accent transition-colors cursor-pointer text-left"
                                                        >
                                                            <img
                                                                src={worker.avatar}
                                                                alt={worker.name}
                                                                loading="lazy"
                                                                decoding="async"
                                                                className="h-9 w-9 sm:h-8 sm:w-8 rounded-full object-cover border border-border/50 shrink-0"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-medium text-foreground">{worker.name}</span>
                                                                    <span className="text-[10px] text-muted-foreground/60 shrink-0 hidden sm:inline">{worker.department}</span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground truncate">{worker.role}</div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            ))}

                                            {/* Ask Poly suggestion */}
                                            <div className="border-t border-border/50">
                                                <a
                                                    href={`${PLATFORM_URL}?ask=${encodeURIComponent(query.trim())}&utm_source=marketing&utm_medium=search`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-accent transition-colors cursor-pointer"
                                                >
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                                                        <MessageCircle className="h-4 w-4 text-primary" aria-hidden="true" />
                                                    </div>
                                                    <span className="text-sm text-foreground">
                                                        Ask Poly: &ldquo;<span className="text-foreground font-medium">{query}</span>&rdquo;
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    ) : hasQuery && Object.keys(groupedResults).length === 0 && filteredTeams.length === 0 ? (
                                        // No results, but show Ask Poly
                                        <div className="py-2">
                                            <div className="px-5 py-3 text-sm text-muted-foreground">
                                                No exact matches found.
                                            </div>
                                            <div className="border-t border-border/50">
                                                <a
                                                    href={`${PLATFORM_URL}?ask=${encodeURIComponent(query.trim())}&utm_source=marketing&utm_medium=search`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-accent transition-colors cursor-pointer"
                                                >
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                                                        <MessageCircle className="h-4 w-4 text-primary" aria-hidden="true" />
                                                    </div>
                                                    <span className="text-sm text-foreground">
                                                        Ask Poly: &ldquo;<span className="text-foreground font-medium">{query}</span>&rdquo;
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        // Default: Popular teams + Popular workers
                                        <div className="py-2">
                                            {POPULAR_TEAMS.length > 0 && (
                                                <>
                                                    <div className="px-5 py-2 flex items-center gap-3">
                                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                            Digital Teams
                                                        </span>
                                                        <span className="text-xs text-muted-foreground/60">
                                                            {teams.length} {teams.length === 1 ? "team" : "teams"}
                                                        </span>
                                                    </div>
                                                    {POPULAR_TEAMS.map((team) => (
                                                        <button
                                                            key={team.slug}
                                                            onClick={() => handleSelectTeam(team)}
                                                            className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-2.5 hover:bg-accent transition-colors cursor-pointer text-left"
                                                        >
                                                            <div className="flex h-9 w-9 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20 shrink-0">
                                                                <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                                    <circle cx="9" cy="7" r="4" />
                                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-medium text-foreground truncate">{team.name}</span>
                                                                    <span className="text-[10px] text-muted-foreground/60 shrink-0 hidden sm:inline">{team.memberCount} workers</span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground truncate">{team.description}</div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                    <div className="border-t border-border/50 mx-3 my-1" />
                                                </>
                                            )}
                                            <div className="px-5 py-2 flex items-center gap-3">
                                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Popular
                                                </span>
                                                <span className="text-xs text-muted-foreground/60">
                                                    {TOTAL_WORKER_COUNT}+ workers
                                                </span>
                                            </div>
                                            {POPULAR_WORKERS.map((worker) => (
                                                <button
                                                    key={worker.name}
                                                    onClick={() => handleSelectWorker(worker)}
                                                    className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-2.5 hover:bg-accent transition-colors cursor-pointer text-left"
                                                >
                                                    <img
                                                        src={worker.avatar}
                                                        alt={worker.name}
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="h-9 w-9 sm:h-8 sm:w-8 rounded-full object-cover border border-border/50 shrink-0"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-foreground">{worker.name}</div>
                                                        <div className="text-xs text-muted-foreground truncate">{worker.role}</div>
                                                    </div>
                                                </button>
                                            ))}

                                            {/* Ask Poly default — always available */}
                                            <div className="border-t border-border/50 mt-1">
                                                <a
                                                    href={`${PLATFORM_URL}?utm_source=marketing&utm_medium=search`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-accent transition-colors cursor-pointer"
                                                >
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                                                        <MessageCircle className="h-4 w-4 text-primary" aria-hidden="true" />
                                                    </div>
                                                    <span className="text-sm text-foreground">
                                                        Ask Poly <span className="text-muted-foreground font-light">— describe the role you need</span>
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Category quick-filter pills */}
                    <motion.div
                        className="relative z-10 flex flex-wrap items-center justify-center gap-2 mt-5"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                    >
                        <span className="text-xs text-muted-foreground/60 mr-1">Hire by category:</span>
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.label}
                                onClick={() => handleCategoryClick(cat.label)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-foreground bg-card border border-border hover:border-primary hover:text-foreground transition-all duration-150 cursor-pointer"
                            >
                                <span>{cat.icon}</span>
                                <span>{cat.label}</span>
                            </button>
                        ))}
                    </motion.div>
                </motion.div>
            </section>

            {/* Results panel */}
            <WorkerResultsPanel
                isOpen={showResults}
                onClose={() => setShowResults(false)}
                query={searchedQuery}
                workers={ALL_WORKERS}
            />
        </div>
    );
}
